/**
 * 與後端 PermissionConstants / AppServiceProvider Gate / PageLevelPermissionTest 對齊。
 *
 * - 170000：冰火副本管理（super，API 可進所有 171000–171700）
 * - 171000–171700：冰火各頁／群組（須明確授權，不自動等同 super）
 * - 171800：保單管理（獨立模組，不屬 170000、不會打開冰火頁）
 * - 180000／190000 等模組 super：階層覆蓋同模組子碼（側欄＋路由）
 * - 900000：平台管理（勿階層全開 90xxxx：事業部／聘階在夥伴營運；金流／Ragic 須明確授權）
 */

export const ICE_FIRE_SUPER = '170000';
export const FORM_SUPER = '180000';
export const INVENTORY_SUPER = '190000';
/** 兌換核銷：櫃台葉碼，不可被 190000 模組 super 代開（有物品規格仍會自動帶 190000） */
export const INVENTORY_REDEEM_EXACT_CODES = ['190900', '190901'];
export const ICE_FIRE_POLICY = '171800';

/** 會員詳情頁閘門；子分頁半選時角色常只存子碼，需一併視為可進詳情 */
export const MEMBER_DETAIL = '100110';
/** 詳情分頁子碼（不含 100118／100119：那是列表視圖／組織編輯，不當作進詳情憑證） */
export const MEMBER_DETAIL_TAB_CODES = [
    '100111',
    '100112',
    '100113',
    '100114',
    '100115',
    '100116',
    '100117',
    '100120'
];
export const MEMBER_DETAIL_ACCESS_CODES = [
    MEMBER_DETAIL,
    ...MEMBER_DETAIL_TAB_CODES,
    // 用戶管理（100106）可進詳情改推薦人
    '100106'
];

export const MEMBER_LIST_VIEW = '100106';
export const MEMBER_PARTNER_ORG_VIEW = '100118';
export const MEMBER_PARTNER_ORG_UPDATE = '100119';
export const MEMBER_ACTIVITY_HISTORY = '100120';

/**
 * 視圖分裂碼：必須明確擁有，不可被 100000 模組階層打開
 *（否則完整後台會誤開「行政會員」「業務會員」選單）
 */
export const MEMBER_VIEW_SPLIT_EXACT_CODES = [
    MEMBER_LIST_VIEW,
    MEMBER_PARTNER_ORG_VIEW,
    MEMBER_PARTNER_ORG_UPDATE
];

/** 完整／行政列表相關（不含業務 100118） */
export const MEMBER_LIST_ACCESS_CODES = [
    MEMBER_LIST_VIEW,
    '100101',
    '100102',
    '100103',
    '100104',
    '100105'
];

export const ICE_FIRE_PAGE_CODES = [
    '171000',
    '171100',
    '171200',
    '171300',
    '171400',
    '171500',
    '171600',
    '171700'
];

/** 冰火群組下的子頁權限碼（可單獨指派） */
export const ICE_FIRE_SUB_PAGE_CODES = [
    '171411',
    '171412',
    '171413',
    '171501',
    '171502',
    '171503',
    '171601',
    '171602'
];

/** 群組碼 → 子頁碼（側欄：擁有群組碼時顯示全部子項，與後端 sync 171500 行為一致） */
export const ICE_FIRE_GROUP_TO_SUB_PAGES = {
    '171400': ['171411', '171412', '171413'],
    '171500': ['171501', '171502', '171503'],
    '171600': ['171601', '171602']
};

export const isIceFirePageCode = (code) => /^171[0-7]00$/.test(String(code));

export const isIceFireGuardedRoute = (routeName) =>
    typeof routeName === 'string' &&
    (routeName.startsWith('IceFire.') || routeName.startsWith('Policy.'));

/** 從 API / store 的權限物件取出 6 位數代碼 */
export const getPermissionCodeFromRecord = (permission) => {
    if (!permission) return '';
    const code =
        permission.code ??
        permission.permission_code ??
        permission.permission ??
        '';
    if (code !== '' && code != null) {
        return String(code);
    }
    const name = permission.name;
    if (name != null && /^\d{6}$/.test(String(name))) {
        return String(name);
    }
    return '';
};

export const buildOwnedCodeSet = (permissions = []) => {
    const set = new Set();
    (permissions || []).forEach((p) => {
        const code = getPermissionCodeFromRecord(p);
        if (code) set.add(code);
    });
    return set;
};

export const canAccessMemberDetail = (ownedCodes) => {
    const owned =
        ownedCodes instanceof Set ? ownedCodes : buildOwnedCodeSet(ownedCodes);
    return MEMBER_DETAIL_ACCESS_CODES.some((code) => owned.has(code));
};

/**
 * 權限階層：模組 XX0000 → 同模組子碼；功能 XXYY00 → XXYY**
 * （冰火 170000、夥伴營運 210000、平台管理 900000／出席 211000 不階層全開，須明確子碼）
 */
const PARTNER_OPS_SUPER = '210000';
const SYSTEM_MODULE = '900000';
const PARTNER_ATTENDANCE_PAGE = '211000';

const hasHierarchicalModuleAccess = (requiredCode, owned) => {
    const code = String(requiredCode);
    if (!/^\d{6}$/.test(code)) return false;

    const moduleCode = `${code.slice(0, 2)}0000`;
    // 冰火／夥伴營運／平台管理 super：勿用 XX0000 一把全開子操作
    if (
        moduleCode !== ICE_FIRE_SUPER &&
        moduleCode !== PARTNER_OPS_SUPER &&
        moduleCode !== SYSTEM_MODULE &&
        owned.has(moduleCode)
    ) {
        return true;
    }

    const functionCode = `${code.slice(0, 4)}00`;
    // 出席管理頁碼不展開到匯入／重算／類型等細項
    if (
        functionCode !== moduleCode &&
        functionCode !== PARTNER_ATTENDANCE_PAGE &&
        owned.has(functionCode)
    ) {
        return true;
    }

    return false;
};

/**
 * @param {string} requiredCode
 * @param {Set<string>|string[]} ownedCodes
 * @param {{ forMenu?: boolean }} options - forMenu：側欄不套用 170000 展開到各冰火頁
 */
export const canAccessPermissionCode = (
    requiredCode,
    ownedCodes,
    options = {}
) => {
    const forMenu = options.forMenu === true;
    const owned =
        ownedCodes instanceof Set ? ownedCodes : buildOwnedCodeSet(ownedCodes);
    const code = String(requiredCode);

    if (owned.has(code)) {
        return true;
    }

    // 兌換核銷群組碼可開掃碼葉碼；不可被 190000 代開
    if (code === '190901' && owned.has('190900')) {
        return true;
    }
    if (INVENTORY_REDEEM_EXACT_CODES.includes(code)) {
        return false;
    }

    // 行政／業務視圖與改組織：禁止 100000 階層代開
    if (MEMBER_VIEW_SPLIT_EXACT_CODES.includes(code)) {
        return false;
    }

    // 會員詳情：任一子分頁即可進詳情（半選父碼 100110 時角色常未寫入父碼）
    if (code === MEMBER_DETAIL && canAccessMemberDetail(owned)) {
        return true;
    }

    // 模組／功能群組階層（表單 180000、背包 190000、商城 150000…）
    if (hasHierarchicalModuleAccess(code, owned)) {
        return true;
    }

    // 擁有群組碼（171500 等）時，子頁碼亦可進入——側欄與路由守衛一致
    for (const [groupCode, subPages] of Object.entries(
        ICE_FIRE_GROUP_TO_SUB_PAGES
    )) {
        if (owned.has(groupCode) && subPages.includes(code)) {
            return true;
        }
    }

    // 170000 僅路由守衛放寬冰火頁；側欄 forMenu 須明確 171xxx
    if (
        !forMenu &&
        isIceFirePageCode(code) &&
        owned.has(ICE_FIRE_SUPER)
    ) {
        return true;
    }

    return false;
};

/** 角色編輯樹：擁有 170000 時，畫面上勾選所有冰火頁面節點 */
/** 是否可讀取冰火活動設定（Layout 橫幅、活動是否啟用） */
export const canFetchIceFireActivitySetting = (permissions = []) => {
    const owned = buildOwnedCodeSet(permissions);
    if (owned.has(ICE_FIRE_SUPER)) {
        return true;
    }
    return (
        ICE_FIRE_PAGE_CODES.some((code) => owned.has(code)) ||
        ICE_FIRE_SUB_PAGE_CODES.some((code) => owned.has(code))
    );
};

export const shouldExpandIceFirePageInRoleTree = (nodeCode, ownedCodes) => {
    const owned =
        ownedCodes instanceof Set ? ownedCodes : buildOwnedCodeSet(ownedCodes);
    if (!nodeCode) return false;
    if (owned.has(String(nodeCode))) return true;
    return isIceFirePageCode(nodeCode) && owned.has(ICE_FIRE_SUPER);
};
